// Phase 16 · SMTP Email settings admin page
import { useEffect, useState } from "react";
import { Save, Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";

export default function EmailSettingsPage() {
  const [form, setForm] = useState({
    smtp_host: "", smtp_port: 587, smtp_user: "",
    smtp_password: "", from_address: "", from_name: "Tulsi Sales OS",
    tls: true, is_active: false,
  });
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [lastTest, setLastTest] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/email-settings");
      const d = r.data || {};
      setForm(f => ({
        ...f,
        smtp_host: d.smtp_host || "",
        smtp_port: d.smtp_port || 587,
        smtp_user: d.smtp_user || "",
        from_address: d.from_address || "",
        from_name: d.from_name || "Tulsi Sales OS",
        tls: d.tls !== false,
        is_active: !!d.is_active,
        smtp_password: "",
      }));
      setPasswordSaved(!!d.password_saved);
    } catch (e) {
      toast.error("Failed to load email settings");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.smtp_password) delete payload.smtp_password;
      await api.patch("/email-settings", payload);
      toast.success("Email settings saved");
      setForm(f => ({ ...f, smtp_password: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async () => {
    if (!testTo) { toast.error("Enter a recipient"); return; }
    setTesting(true); setLastTest(null);
    try {
      const r = await api.post("/email-settings/test", { to_address: testTo });
      setLastTest({ ok: true, sent_at: r.data.sent_at });
      toast.success("Test email sent");
    } catch (e) {
      setLastTest({ ok: false, error: e?.response?.data?.detail || String(e) });
      toast.error("Test send failed");
    } finally { setTesting(false); }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="email-settings-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Settings" }, { label: "Email settings" }]}
        title="Email settings"
        subtitle="Email settings and notification message templates."
        favorite={{ key: "admin.email-settings", type: "screen", label: "Email settings", route: "/admin/email-settings" }}
        testid="emailsettingspage-page-header"
      />

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Mail className="w-6 h-6 text-[#E5197F]" /> Email / SMTP Settings
        </h1>
        <p className="text-xs text-neutral-500 mt-1">
          Configure the outbound SMTP relay used for approval notifications and scheduled reports.
        </p>
      </div>

      <div className="bg-white rounded-xl border p-5 space-y-3">
        {[
          ["smtp_host", "SMTP host", "text", "smtp.gmail.com"],
          ["smtp_port", "SMTP port", "number", "587"],
          ["smtp_user", "SMTP username", "text", "your-account@gmail.com"],
          ["smtp_password", "SMTP password (leave blank to keep saved value)", "password", passwordSaved ? "•••••• (saved)" : ""],
          ["from_address", "From address", "email", "noreply@yourdomain.com"],
          ["from_name", "From name", "text", "Tulsi Sales OS"],
        ].map(([key, label, type, placeholder]) => (
          <label key={key} className="block">
            <div className="text-xs font-medium text-neutral-700 mb-1">{label}</div>
            <input
              type={type}
              value={form[key] ?? ""}
              onChange={e => setForm({ ...form, [key]: type === "number" ? Number(e.target.value) : e.target.value })}
              placeholder={placeholder}
              data-testid={`email-${key}-input`}
              className="w-full h-10 px-3 rounded border border-neutral-200 text-sm"
            />
          </label>
        ))}
        <div className="flex items-center gap-4 pt-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.tls} onChange={e => setForm({...form, tls: e.target.checked})}
                    data-testid="email-tls-checkbox" />
            Use STARTTLS
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                    data-testid="email-active-checkbox" />
            Active (enable dispatch)
          </label>
        </div>
        <button onClick={save} disabled={saving} data-testid="email-save-btn"
                className="w-full h-10 rounded-lg bg-[#E5197F] text-white font-medium text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
        </button>
      </div>

      <div className="bg-white rounded-xl border p-5 space-y-3">
        <div className="font-semibold text-sm">Send test email</div>
        <input value={testTo} onChange={e => setTestTo(e.target.value)}
                placeholder="you@example.com" type="email"
                data-testid="email-test-to-input"
                className="w-full h-10 px-3 rounded border border-neutral-200 text-sm" />
        <button onClick={test} disabled={testing || !form.is_active}
                data-testid="email-test-btn"
                className="w-full h-10 rounded-lg bg-indigo-600 text-white font-medium text-sm inline-flex items-center justify-center gap-1 disabled:opacity-50">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send test</>}
        </button>
        {lastTest && (
          <div className={`text-xs p-2 rounded border ${lastTest.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-rose-50 border-rose-200 text-rose-700"}`}
                data-testid="email-test-result">
            {lastTest.ok ? `✓ Sent at ${lastTest.sent_at}` : `✗ ${lastTest.error}`}
          </div>
        )}
      </div>
    </div>
  );
}
